import * as React from "react";

import "./exhibit-xpresso.scss";

import {cs}                                      from "@Common/react/chain-services";
import {consumeContext, provideContext}          from "@Common/react/context";
import {fragments}                               from "@Common/react/fragments";
import {keyed}                                   from "@Common/react/keyed";
import {Load}                                    from "@Common/react/load";
import {UseState}                                from "@Common/react/use-state";
import {CcIntegration}                           from "@Common/tools/cc-integration/cc-integration";
import {Button}                                  from "@Common/tools/cc-integration/ui/button/button";
import {ProdFileDownload}                        from "@Common/tools/cc-integration/ui/prod-file-download/prod-file-download";
import {WarningPanel}                            from "@Common/tools/cc-integration/ui/warning-panel/warning-panel";
import {WatermarkDownload}                       from "@Common/tools/cc-integration/ui/watermark-download/watermark-download";
import {PhotoDashboard}                          from "@Common/ui-components/photo-dashboard/photo-dashboard";
import {AlertModal}                              from "@Common/ui-components/alert-modal/alert-modal";
import {changeNestedValue, getNestedValue, snv}  from "@Common/utils/arr-path";
import {chain}                                   from "@Common/utils/fs";
import {isEmpty}                                 from "@Common/utils/objects";
import {ConfirmDialog}                           from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";

import {SocialDownloadBtn} from "../../../../../common/components/social-download-btn/social-download-btn";

import {Instruction}         from "../../../common/instruction/instruction";
import {ActiveSaver}         from "../active-saver";

import {collectUserData}     from "./collect-user-data";
import {Controls}            from "./controls/controls";
import {EditForm}            from "./edit-form/edit-form";

export const ExhibitXpresso = ({templateIds, ccValues, componentId, inputData, item, listing, advEditMode, mockData, templateVariations, next}) => cs(
    consumeContext("apis"),
    consumeContext("templateOptions"),
    consumeContext("menus"),

    ["userData", (_, next) =>
        collectUserData({ccStateId: ccValues.ccStateId, ccSourceStateId: item.metaData?.ccSourceStateId, userCreatedStateId: ccValues.userCreatedStateId, templateIds, listing, mockData, next})
    ],
    ({userData}, next) => provideContext({"userData": userData}, next),
    ["suggestedComments", ({apis, templateOptions, menus}, next) => Load({
        fetch: async () => {
            let subId = templateOptions?.subCategories.frontTemplate.id;
            let programId = null;
            if (subId && menus) {
                const regExp = new RegExp(`^app/cat/[^/]+/sub/${subId}$`);
                for (let i = 0; i < menus.leftMenus.length; i++) {
                    const leftMenu = menus.leftMenus[i];

                    for (let j = 0; leftMenu.subMenu && j < leftMenu.subMenu.length; j++) {
                        const subMenu = leftMenu.subMenu[j];
                        if (subMenu.idProgram && regExp.test( subMenu.link )) {
                            programId = subMenu.idProgram;
                        }
                    }
                }
            }
            return await apis.next.session.getSuggestedComments({request: {
                    idTemplate: templateIds[0],
                    idProgram: programId,
                    orderType: null, // null will be treated as "Any" - other types are: justListedComments, justSoldComments
                    /*
                    // the following are previous values that were being set, but are not currently supported by the web
                    // service - leaving in place in case this was in prep for additional parameters to be later added
                    // to the service - when re-enabling, be sure to add "userData" to the parameter list for this cs
                    // item
                    styleId: templateOptions?.styles.frontTemplate.id, // is this used?
                    productId: templateOptions?.product?.productId,
                    agentId: userData.sourceData.xd.user.idPrimary,
                    branchId: userData.sourceData.xd.office.idPrimary,
                    companyId: userData.sourceData.xd.company.idCompany,
                    programId: programId, // possibly means idProgram?
                    */
                }})
        },
        next
    })],
    ({suggestedComments}, next) => provideContext({"suggestedComments": suggestedComments}, next),
    ["pickpackStyles", (_,next) => next(
        chain(
            item?.selections?.options?.find((o) => o.dev_key === "pick_pack_options"),
            (_) => _ && JSON.parse(_.name),
        )
    )],
    ["cc", ({pickpackStyles, userData}, next) => CcIntegration({
        userData,
        useAdvancedEditingMode: advEditMode,
        alwaysEdit: true,
        inputData,
        itemId: item?.itemId,
        ccValues,
        componentId,
        pickpackOptions: item?.options?.pickpackOptions,
        pickpackStyles,
        templateVariations,
        distributionType: item?.selections?.distributionType,
        qrCodeLineItemValue: item?.selections?.options?.find(x => x.dev_key === "qr_code")?.subOptionDevKey,
        qrCodeUrlValue: item?.selections?.options?.find(x => x.dev_key === "qr_code")?.subOptionDevKey ? item?.tracking?.redirectUrl : '',
        next,
    })],
    ["leavingWarningModal", ({}, next) => ConfirmDialog({next})],
    ["noPhotoOptionsWarningModal", (_, next) => AlertModal({
        next,
        header: "No photo gallery options currently available on this template",
        message: <>
                <p>Please email or call our support team</p>
                <p>
                    Support Email: <span className="info-span">info@xpressdocs.com</span><br/>
                    Support Phone: <span className="info-span">(866) 977-3627</span>
                </p>
            </>,
        cancelButton: false,
        xdQaName: "missingGalleryModal",
    })],
    ["photoDashboard", ({}, next) => PhotoDashboard({next})],
    ({cc, photoDashboard, noPhotoOptionsWarningModal}, next) => fragments(
        next(),
        cc.registerCustomGallery({
            onOpen: (modalData) => {

                // show modal for no options if there are no image sources available
                if (!modalData.imageArea.allowedTabs.length) {
                    return noPhotoOptionsWarningModal.show();
                }

                // open photo dashboard
                return photoDashboard.show(modalData);

            },
        }),
    ),
    ["itemSaver", ({cc, apis, templateOptions}, next) => ActiveSaver({
        getInitBuffer: () => loadTemplateFromItem(item, componentId),
        getLiveData: () => ({
            ccHost: cc.ccHost,
            ccVersion: cc.ccVersion,
            ccStateId: cc.ccStateId,
            proofImages: cc.proofImages,
            highResProducts: cc.highResProducts,
            inputData: cc.inputData,
            pickpackOptions: cc.pickpackOptions,
            redirectType: cc.inputData?.qrValues?.tracking?.redirectType,
            redirectUrl: cc.inputData?.qrValues?.tracking?.redirectUrl
        }),
        disabled: (!cc.ccStateId || !cc.highResProducts || !cc.proofImages || cc.changed),
        doSave: (buffer) => apis.next.item.updateItem(setTemplateToItem({
            componentId,
            template: buffer,
            item,
            templateOptions
        })),
        next,
    })],
    ["disableSaves", (_, next) => UseState({
        defaultValue: false,
        next,
    })],
    ({cc, disableSaves, itemSaver, leavingWarningModal, templateOptions}) => <>
        {next({
            ccHost: cc.ccHost,
            ccStateId: cc.ccStateId,
            ccVersion: cc.ccVersion,
            currentSideIndex: cc.currentSideIndex,
            changed: cc.changed,
            changeSide: cc.changeSide,
            disableSaves,
            initialized: cc.initialized,
            fullScreen: cc.fullScreen,
            //proof images will be available outside the CC Component
            proofImages: cc.proofImages,
            render: () => (
                <div className="exhibit-xpresso-9dd">
                    {cc.render({heightPerWidthRatio: .7})}

                    {cc.fullScreen && <div className="fullScreenBackdrop"/>}

                    {advEditMode && (
                        <div style={{marginTop: "5px", display: "flex"}}>
                            <div className="infoDiv">
                                {Instruction({
                                    text: "Click on the template to make edits directly",
                                })}
                            </div>
                            <div className="fullScreenDiv">
                                <Button
                                    buttonText="Enter Full Screen Mode"
                                    xdQaName="Button Full Screen Mode"
                                    className="fullScreenButton"
                                    onClick={cc.openFullScreenMode}
                                />
                            </div>
                        </div>
                    )}

                    <div className="warning-margin">
                        {cc.getWarnings().slice(0,3).map( (item, index) => cs(
                            keyed(index),
                            () => WarningPanel({
                                content: {item: item, cc: cc},
                            })
                        ))}
                    </div>
                </div>
            ),
            watermarkDownload:  WatermarkDownload({
                itemId: item.itemId,
                ccValues: {...ccValues, ccHost: cc.ccHost, ccVersion: cc.ccVersion},
                disableSaves,
                loadWatermarkPdf: async ()=> {
                    let watermarkPdfs = await cc.loadWatermarkPdf();
                    await itemSaver.save();
                    return watermarkPdfs;
                },
            }),
            prodFileDownload: ProdFileDownload({
                highResProducts:cc.highResProducts,
                sides: cc.sides,
                changed: cc.changed,
                disableSaves,
                itemId: item.itemId,
                loadProdFiles: async ()=>{
                    let prodFiles = await cc.loadProdFiles();
                    await itemSaver.save();
                    return prodFiles;
                },
            }),
            socialDownloadBtn: SocialDownloadBtn({
                changed: cc.changed,
                highResProducts: cc.highResProducts,
                itemName: item.itemName,
                itemId: item.itemId,
                templateId: templateIds[0],
                templateOptions,
                loadProdFiles: async ()=>{
                    let prodFiles = await cc.loadProdFiles();
                    await itemSaver.save();
                    return prodFiles;
                },
            }),
            controls: Controls({cc, itemSaver}),
            editForm: EditForm({
                cc,
                disableSaves,
                advEditMode: advEditMode,
                itemId: item.itemId,
                distributionType: item?.selections?.distributionType,
                itemSaver
            }),
            highResProducts: cc.highResProducts,
            save: async() => {
                let saved = await cc.save();
                await itemSaver.save();
                return saved;
            },
            getMergeFieldErrors: cc.getMergeFieldErrors,
            getMergeFieldErrorMessageJsx: cc.getMergeFieldErrorMessageJsx,
            loadProofs: cc.loadProofs,
            clearChanged: cc.clearChanged,
            showWarning: async () => {
                if (!cc.changed) {
                    return true;
                } else {
                    return await leavingWarningModal.show({
                        message: "Some of your changes may not be saved. Do you want to continue?",
                        actionContent: "Please click Cancel to return to editing",
                        buttons: {
                            confirmLabel: "OK",
                        },
                    });
                }
            },
            inputData: cc.inputData,
            isRequired: cc.isRequired,
            openFullScreenMode: cc.openFullScreenMode,
            getProductSurfacesWithImages: cc.getProductSurfacesWithImages,
            importImages: cc.importImages,
            surfaceHasAiFields: async () => {
                const eligibleFieldsPresent = await cc.surfaceHasAiFields();
                return eligibleFieldsPresent;
            },
        })}
    </>,
);

const loadTemplateFromItem = (item, componentId) => (
    componentId ? {
        ccHost          : item?.components?.[componentId]?.metaData?.ccHost,
        ccStateId       : item?.components?.[componentId]?.metaData?.ccReference,
        ccVersion       : item?.components?.[componentId]?.metaData?.ccVersion,
        highResProducts : item?.components?.[componentId]?.metaData?.ccReferenceURLs,
        inputData       : item?.components?.[componentId]?.metaData?.inputData,
        proofImages     : item?.components?.[componentId]?.metaData?.ccProofUrls,
        pickpackOptions : item?.components?.[componentId]?.options?.pickPackOptions,
        redirectType    : item?.components?.[componentId]?.tracking?.redirectType,
        redirectUrl     : item?.components?.[componentId]?.tracking?.redirectUrl,
    } : {
        ccHost          : item.metaData?.ccHost,
        ccStateId       : item.metaData?.ccReference,
        ccVersion       : item.metaData?.ccVersion,
        highResProducts : item.metaData?.ccReferenceURLs,
        inputData       : item.metaData?.inputData,
        proofImages     : item.metaData?.ccProofUrls,
        pickpackOptions : item.options?.pickPackOptions,
        redirectType    : item?.tracking?.redirectType,
        redirectUrl     : item?.tracking?.redirectUrl,
    }
);

const setTemplateToItem = ({componentId, template, item, templateOptions}) => {
    let componentPath = componentId ? ["components", componentId] : [];
    return chain(
        item,
        (_) => snv(_, [...componentPath, "metaData", "ccHost"         ], template.ccHost),
        (_) => snv(_, [...componentPath, "metaData", "ccReference"    ], template.ccStateId),
        (_) => snv(_, [...componentPath, "metaData", "ccReferenceURLs"], template.highResProducts),
        (_) => snv(_, [...componentPath, "metaData", "ccVersion"      ], template.ccVersion),
        (_) => snv(_, [...componentPath, "metaData", "inputData"      ], template.inputData),
        (_) => snv(_, [...componentPath, "metaData", "ccProofUrls"    ], template.proofImages),
        (_) => snv(_, [...componentPath, "options",  "pickpackOptions"], template.pickpackOptions),
        (_) => snv(_, [...componentPath, "itemThumbnails"], template.proofImages),
        (_) => snv(_, [...componentPath, "tracking"], {
            redirectType: template.redirectType,
            redirectUrl: template.redirectUrl,
        }),
        (_) => {
            //empty object is becoming an array with snv, also don't want to override
            if(!template.redirectType && !template.redirectUrl) {
                if(componentId){
                    delete _.components?.[componentId]?.tracking?.redirectType;
                    delete _.components?.[componentId]?.tracking?.redirectUrl;
                    if(_.components?.[componentId]?.tracking && !Object.keys(_.components?.[componentId]?.tracking).length) {
                        delete _.components?.[componentId]?.tracking;
                    }
                }
                else {
                    delete _.tracking?.redirectType;
                    delete _.tracking?.redirectUrl;
                    if(_.tracking && !Object.keys(_.tracking).length) {
                        delete _.tracking;
                    }
                }
            }
            if( templateOptions?.options?.find(x => x.dev_key === "qr_code") ) {
                let index = _?.selections?.options?.findIndex(x => x?.dev_key === "qr_code");
                if( template.redirectType ) {
                    if(templateOptions.options.find(x => x?.dev_key === "qr_code")?.subOptions?.find(y => y?.dev_key == template.redirectType)) {
                        if( index >= 0 ) {
                            _ = snv(_, ["selections", "options", index, 'subOptionDevKey'], template.redirectType);
                        }
                        else {
                            _.selections.options.push({'dev_key': 'qr_code', subOptionDevKey: template.redirectType});
                        }
                    }
                    else {
                        if(index >= 0) delete _.selections.options[index];
                    }
                }
                else {
                    if(index >= 0) delete _.selections.options[index];
                }
            }
            console.log(_)
            return _;
        },
        (_) => changeNestedValue(_, [...componentPath, "selections", "options"], (_) => {
            let pickPackOptionSelection = _?.find((o) =>
                o?.dev_key == "pick_pack_options"
            );
            if(
                !pickPackOptionSelection ||
                getNestedValue(item, [...componentPath, "selections"])?.variationType !== "master"
            ) {
                return _;
            }
            let pickPackOptions = JSON.parse(pickPackOptionSelection.name);
            if(isEmpty(pickPackOptions)) {
                return _;
            }
            for(let pickPackOption of pickPackOptions) {
                if(template.inputData?.['text']?.[pickPackOption.surfaceName]?.[pickPackOption.fieldName]) {
                    pickPackOption.vendorValue =
                        template.inputData['text'][pickPackOption.surfaceName][pickPackOption.fieldName];
                }
            }
            return [
                ..._?.filter((o) =>
                    o.dev_key !== "pick_pack_options"
                ) ?? [],
                {
                    "name": JSON.stringify(pickPackOptions),
                    "dev_key": "pick_pack_options",
                },
            ];
        })
    )
};
